import { Component } from 'react';
import type { ReactChild } from 'react';
import Bugsnag from '@bugsnag/js';
import { GenericAppError } from './components/generic/AppError';
import { window } from './globals';

type DefaultErrorBoundaryProps = {
  children: ReactChild;
};

type DefaultErrorBoundaryState = {
  hasError: boolean;
};

/**
 * Catches Errors which originate in the component tree below it.
 * Displays GenericAppError if there is an error, otherwise renders the children.
 *
 * Note: Must be a class Component in order to use getDerivedStateFromError.
 */
export class DefaultErrorBoundary extends Component<DefaultErrorBoundaryProps, DefaultErrorBoundaryState> {
  state = {
    hasError: false,
  }

  static getDerivedStateFromError() {
    return {
      hasError: true,
    };
  }

  render() {
    const { children } = this.props;
    const { hasError } = this.state;
    return hasError
      ? <GenericAppError />
      : children;
  }
}

/**
 * Use the BugsnagErrorBoundary if bugsnag is enabled,
 * otherwise fall back to the DefaultErrorBoundary.
 *
 * @param {ReactChild} children - The rest of the component tree.
 */
const ErrorBoundary = ({ children }: { children: ReactChild }) => {
  const BugsnagErrorBoundary = window.bugsnag?.enabled
    ? Bugsnag.getPlugin('react')
    : DefaultErrorBoundary;

  return (
    <BugsnagErrorBoundary FallbackComponent={GenericAppError}>
      {children}
    </BugsnagErrorBoundary>
  );
}

export default ErrorBoundary;
